// Beams, guide arrow and the chapter list.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { scene } from '../core/scene.js';
import { M, add, canvasTex } from '../lib/factory.js';
import { colliders } from '../world/eixample.js';
import { logoTex } from '../world/landmarks.js';
import { groundHeight } from './physics.js';
import { nextChapter } from '../rules/chapters.js';
import { STOPS } from '../data/stops.js';
import { applyLang, tr } from '../ui/lang.js';
import { state } from '../core/state.js';
import { chapters } from '../dom.js';

export let visited, stopPrompt, arrow, nextStop, renderChapters;

export function buildStopMarkers() {
  renderChapters = function renderChapters() {
    const n = nextStop();
    chapters.innerHTML =
      `<b>${tr('chapters')} ${visited.size}/${STOPS.length}</b>` +
      STOPS.map(
        (st, i) =>
          `<div class="${visited.has(i) ? 'done' : i === n ? 'next' : 'locked'}">${visited.has(i) ? '✓' : i === n ? i + 1 : '🔒'} · ${st.place}</div>`,
      ).join('');
  };
  visited = new Set();
  stopPrompt = document.getElementById('stopPrompt');
  const beamTex = canvasTex(4, 64, (c) => {
    const g = c.createLinearGradient(0, 0, 0, 64);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(1, 'rgba(255,255,255,.55)');
    c.fillStyle = g;
    c.fillRect(0, 0, 4, 64);
  });
  beamTex.wrapS = beamTex.wrapT = THREE.ClampToEdgeWrapping;
  STOPS.forEach((st, i) => {
    const g = new THREE.Group();
    g.position.set(st.x, groundHeight(st.x, st.z) + 0.02, st.z);
    scene.add(g);
    st.g = g;
    st.ringMat = new THREE.MeshBasicMaterial({
      color: st.color,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    st.ring = new THREE.Mesh(new THREE.RingGeometry(1.25, 1.6, 48).rotateX(-PI / 2), st.ringMat);
    g.add(st.ring);
    st.beam = new THREE.Mesh(
      new THREE.CylinderGeometry(1.3, 1.3, 9, 32, 1, true),
      new THREE.MeshBasicMaterial({
        color: st.color,
        map: beamTex,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    st.beam.position.y = 4.5;
    g.add(st.beam);
    const hex = '#' + st.color.toString(16).padStart(6, '0');
    st.label = document.createElement('div');
    st.label.className = 'badge';
    st.label.innerHTML = `<i style="background:${hex}">${i + 1}</i><span>${st.place}<small>${st.k}</small></span>`;
    document.body.appendChild(st.label);
  });
  // a Welodata totem sign beside the Torre Glòries stop, facing the city
  {
    const g = new THREE.Group();
    g.position.set(42.6, 0.3, -49.2);
    g.rotation.y = Math.atan2(-42.6, 49.2);
    scene.add(g);
    const face = M(0xffffff, {
        map: logoTex('assets/logos/welodata.png', 1024, 240, '#ffffff', 0.08),
        emissiveMap: null,
        flatShading: false,
      }),
      frame = M(0x2b2f33, { flatShading: false });
    add(
      new THREE.BoxGeometry(2.6, 0.62, 0.12),
      [frame, frame, frame, frame, face, face],
      0,
      1.5,
      0,
      g,
    );
    for (const sx of [-1, 1])
      add(new THREE.CylinderGeometry(0.05, 0.06, 1.25, 8), frame, sx * 1.05, 0.62, 0, g);
    add(new THREE.BoxGeometry(2.8, 0.1, 0.3), frame, 0, 0.05, 0, g);
    colliders.push({ type: 'ell', cx: 42.6, cz: -49.2, a: 1.4, b: 1.4 });
  }
  // guide arrow: a chunky bevelled 3D arrow floating over the scooter, pointing to the next chapter in that stop's colour,
  // with a white core stripe; it turns smoothly and bobs, and a soft glow disc under it keeps it readable on any background
  arrow = new THREE.Group();
  arrow.visible = false;
  scene.add(arrow);
  {
    const sh = new THREE.Shape(); // tip at +y
    sh.moveTo(0, 0.95);
    sh.lineTo(0.62, 0.2);
    sh.lineTo(0.24, 0.2);
    sh.lineTo(0.24, -0.75);
    sh.lineTo(-0.24, -0.75);
    sh.lineTo(-0.24, 0.2);
    sh.lineTo(-0.62, 0.2);
    sh.closePath();
    const body = new THREE.ExtrudeGeometry(sh, {
      depth: 0.16,
      bevelEnabled: true,
      bevelSize: 0.06,
      bevelThickness: 0.06,
      bevelSegments: 4,
      curveSegments: 4,
    })
      .translate(0, 0, -0.08)
      .rotateX(PI / 2);
    arrow.userData.mat = M(0xffc24a, {
      emissive: 0xffa21a,
      emissiveIntensity: 0.55,
      flatShading: false,
      roughness: 0.35,
    });
    const m = new THREE.Mesh(body, arrow.userData.mat);
    m.castShadow = false;
    arrow.add(m);
    const st = new THREE.Shape();
    st.moveTo(0, 0.62);
    st.lineTo(0.3, 0.27);
    st.lineTo(0.09, 0.27);
    st.lineTo(0.09, -0.6);
    st.lineTo(-0.09, -0.6);
    st.lineTo(-0.09, 0.27);
    st.lineTo(-0.3, 0.27);
    st.closePath();
    arrow.add(
      new THREE.Mesh(
        new THREE.ShapeGeometry(st)
          .rotateX(-PI / 2)
          .rotateY(PI)
          .translate(0, 0.15, 0),
        M(0xffffff, { emissive: 0xffffff, emissiveIntensity: 0.35, flatShading: false }),
      ),
    );
    const glowTexA = canvasTex(64, 64, (c) => {
      const g = c.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,220,140,.55)');
      g.addColorStop(1, 'rgba(255,220,140,0)');
      c.fillStyle = g;
      c.fillRect(0, 0, 64, 64);
    });
    const glow = new THREE.Mesh(
      new THREE.PlaneGeometry(2.6, 2.6).rotateX(-PI / 2).translate(0, -0.12, 0),
      new THREE.MeshBasicMaterial({
        map: glowTexA,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    arrow.add(glow);
    arrow.scale.setScalar(1.05);
    // drawn over everything (trees, awnings) so the guide is never hidden
    arrow.traverse((o) => {
      if (o.isMesh) {
        o.material.depthTest = false;
        o.renderOrder = 10;
      }
    });
    glow.renderOrder = 9;
  }
  state.nearStop = -1;
  nextStop = () => nextChapter(STOPS.length, visited);
  renderChapters();
  applyLang();
}
